import { useI18n } from '../../context/I18nContext';
import SingleCableVisualization from './SingleCableVisualization';
import MultipleCableVisualization from './MultipleCableVisualization';
import MultipolarCableVisualization from './MultipolarCableVisualization';
import SheathCableVisualization from './SheathCableVisualization';
import {
    analyzeAndGroupCables,
    endpointsCanConnect,
    getCableConnectionEndpoints,
    getMultipolarPoleConnections
} from '../../utils/cableLogic';

const SORT_BY_INDEX = (a, b) => (a.originalIndex ?? 0) - (b.originalIndex ?? 0);

const createEmptyBranchState = () => ({ A: [], B: [] });

const dedupeBranchEntries = (entries) => {
    const seen = new Set();

    return entries.filter((entry) => {
        const branchCable = entry?.cable;
        if (!branchCable) return false;

        const key = `${branchCable.originalIndex}-${entry.side}`;
        if (seen.has(key)) {
            return false;
        }

        seen.add(key);
        return true;
    });
};

const buildStandardCableBranches = (anchorCable, candidateCables, settings) => {
    const anchorEndpoints = getCableConnectionEndpoints(anchorCable);
    const branchState = createEmptyBranchState();

    candidateCables.forEach((candidate) => {
        if (!candidate || candidate.originalIndex === anchorCable.originalIndex || (candidate.poli && Array.isArray(candidate.poli))) {
            return;
        }

        const candidateEndpoints = getCableConnectionEndpoints(candidate);

        anchorEndpoints.forEach((anchorEndpoint) => {
            candidateEndpoints.forEach((candidateEndpoint) => {
                if (endpointsCanConnect(anchorEndpoint, candidateEndpoint, settings)) {
                    branchState[anchorEndpoint.side].push({
                        cable: candidate,
                        side: candidateEndpoint.side
                    });
                }
            });
        });
    });

    return {
        A: dedupeBranchEntries(branchState.A).sort((left, right) => SORT_BY_INDEX(left.cable, right.cable)),
        B: dedupeBranchEntries(branchState.B).sort((left, right) => SORT_BY_INDEX(left.cable, right.cable))
    };
};

const getBranchedGroupDescriptor = (groupCables, settings, preferredAnchors = []) => {
    const candidates = preferredAnchors.length > 0 ? preferredAnchors : groupCables;

    for (const anchorCable of candidates) {
        const otherCables = groupCables.filter(cable => cable.originalIndex !== anchorCable.originalIndex);
        const branches = buildStandardCableBranches(anchorCable, otherCables, settings);

        if (branches.A.length === otherCables.length) {
            return {
                anchorCable,
                branchSide: 'A',
                branches: branches.A,
                allBranches: branches
            };
        }

        if (branches.B.length === otherCables.length) {
            return {
                anchorCable,
                branchSide: 'B',
                branches: branches.B,
                allBranches: branches
            };
        }
    }

    return null;
};

const collectConsumedIndexesFromEntries = (entries = [], consumedIndexes = new Set()) => {
    entries.forEach((entry) => {
        if (!entry?.cable) return;
        consumedIndexes.add(entry.cable.originalIndex);
        collectConsumedIndexesFromEntries(entry.children || [], consumedIndexes);
    });

    return consumedIndexes;
};

const buildNestedBranchEntries = ({
    sourceEndpoint,
    candidateCables,
    settings,
    candidateEndpointMap,
    excludedIndexes = new Set()
}) => {
    if (!sourceEndpoint?.connection) {
        return [];
    }

    const getOppositeEndpoint = (candidate, side) => {
        const endpoints = candidateEndpointMap.get(candidate.originalIndex) || [];
        return endpoints.find((endpoint) => endpoint.side !== side) || null;
    };

    const directEntries = candidateCables
        .filter((candidate) =>
            candidate &&
            !excludedIndexes.has(candidate.originalIndex) &&
            !(candidate.poli && Array.isArray(candidate.poli))
        )
        .map((candidate) => {
            const endpoints = candidateEndpointMap.get(candidate.originalIndex) || [];
            const matchingEndpoint = endpoints.find((endpoint) =>
                endpointsCanConnect(sourceEndpoint, endpoint, settings)
            );

            if (!matchingEndpoint) {
                return null;
            }

            const nextExcluded = new Set(excludedIndexes);
            nextExcluded.add(candidate.originalIndex);

            const oppositeEndpoint = getOppositeEndpoint(candidate, matchingEndpoint.side);

            return {
                cable: candidate,
                side: matchingEndpoint.side,
                children: buildNestedBranchEntries({
                    sourceEndpoint: oppositeEndpoint,
                    candidateCables,
                    settings,
                    candidateEndpointMap,
                    excludedIndexes: nextExcluded
                })
            };
        })
        .filter(Boolean);

    return dedupeBranchEntries(directEntries)
        .sort((left, right) => SORT_BY_INDEX(left.cable, right.cable));
};

const buildSheathBranches = (groupCables, settings, preferredAnchors = []) => {
    const candidates = preferredAnchors.length > 0 ? preferredAnchors : groupCables;
    const candidateEndpointMap = new Map(
        groupCables.map((candidate) => [
            candidate.originalIndex,
            getCableConnectionEndpoints(candidate)
        ])
    );

    for (const anchorCable of candidates) {
        if (!anchorCable) continue;

        const otherCables = groupCables.filter(cable => cable.originalIndex !== anchorCable.originalIndex);
        const anchorEndpoints = getCableConnectionEndpoints(anchorCable);
        const anchorEndpointA = anchorEndpoints.find((endpoint) => endpoint.side === 'A');
        const anchorEndpointB = anchorEndpoints.find((endpoint) => endpoint.side === 'B');

        const excludedIndexes = new Set([anchorCable.originalIndex]);
        const branchesA = buildNestedBranchEntries({
            sourceEndpoint: anchorEndpointA,
            candidateCables: otherCables,
            settings,
            candidateEndpointMap,
            excludedIndexes
        });

        collectConsumedIndexesFromEntries(branchesA, excludedIndexes);

        const branchesB = buildNestedBranchEntries({
            sourceEndpoint: anchorEndpointB,
            candidateCables: otherCables,
            settings,
            candidateEndpointMap,
            excludedIndexes
        });

        const consumedIndexes = collectConsumedIndexesFromEntries(branchesB, excludedIndexes);

        if (consumedIndexes.size === groupCables.length) {
            return {
                anchorCable,
                allBranches: {
                    A: branchesA,
                    B: branchesB
                }
            };
        }
    }

    return null;
};

const buildMultipolarBranches = (multipolarCable, candidateCables, settings) => {
    const poleConnections = getMultipolarPoleConnections(multipolarCable, candidateCables, settings);
    const branchMap = {};
    const consumedIndexes = new Set();
    const candidateEndpointMap = new Map(
        candidateCables.map((candidate) => [
            candidate.originalIndex,
            getCableConnectionEndpoints(candidate)
        ])
    );

    poleConnections.forEach(({ poloIndex }) => {
        const poleEndpoints = getCableConnectionEndpoints(multipolarCable);
        const poleEndpointA = poleEndpoints.find((endpoint) =>
            endpoint.poloIndex === poloIndex && endpoint.side === 'A'
        );
        const poleEndpointB = poleEndpoints.find((endpoint) =>
            endpoint.poloIndex === poloIndex && endpoint.side === 'B'
        );

        const directA = buildNestedBranchEntries({
            sourceEndpoint: poleEndpointA,
            candidateCables,
            settings,
            candidateEndpointMap,
            excludedIndexes: new Set([multipolarCable.originalIndex])
        });
        const directB = buildNestedBranchEntries({
            sourceEndpoint: poleEndpointB,
            candidateCables,
            settings,
            candidateEndpointMap,
            excludedIndexes: new Set([multipolarCable.originalIndex])
        });

        const branchesA = directA.length > 0 ? directA : [];
        const branchesB = directB.length > 0 ? directB : [];

        if (branchesA.length > 0 || branchesB.length > 0) {
            branchMap[poloIndex] = {
                A: branchesA,
                B: branchesB
            };

            collectConsumedIndexesFromEntries(branchesA, consumedIndexes);
            collectConsumedIndexesFromEntries(branchesB, consumedIndexes);
        }
    });

    return { branchMap, consumedIndexes };
};

const HybridCableVisualization = ({
    cables,
    settings,
    siglatura,
    hoveredCable,
    setHoveredCable,
    isMobile,
    availableSheaths = [],
    processedSubGroups = null,
    forcedSheathId = null
}) => {
    const { t } = useI18n();

    const multipolarCables = cables.filter(cable => cable.poli && Array.isArray(cable.poli));
    const standardCables = cables.filter(cable => !(cable.poli && Array.isArray(cable.poli)));
    const allSubGroups = Array.isArray(processedSubGroups) && processedSubGroups.length > 0
        ? processedSubGroups
            .filter(group => group && (group.type === 'single' || group.type === 'multiple'))
            .map(group => ({
                ...group,
                cables: (group.cables || [])
                    .filter(cable => !(cable.poli && Array.isArray(cable.poli)))
                    .sort(SORT_BY_INDEX),
                isSingle: group.type === 'single'
            }))
            .filter(group => group.cables.length > 0)
        : analyzeAndGroupCables(standardCables, settings);

    const multipolarBranchMap = new Map();
    const consumedStandardIndexes = new Set();

    multipolarCables.forEach((cable) => {
        const { branchMap, consumedIndexes } = buildMultipolarBranches(cable, standardCables, settings);
        multipolarBranchMap.set(cable.originalIndex, branchMap);
        consumedIndexes.forEach(index => consumedStandardIndexes.add(index));
    });

    const renderItems = [];
    const sheathGroups = new Map();

    const appendToSheathGroup = (cable, branchState = null, sheathIdOverride = null) => {
        const sheathId = sheathIdOverride ?? cable?.guaina;
        if (!sheathId) return;

        if (!sheathGroups.has(sheathId)) {
            sheathGroups.set(sheathId, {
                sheathId,
                cables: [],
                cableBranches: {}
            });
        }

        const sheathGroup = sheathGroups.get(sheathId);
        const alreadyPresent = sheathGroup.cables.some(existing => existing.originalIndex === cable.originalIndex);

        if (!alreadyPresent) {
            sheathGroup.cables.push(cable);
            sheathGroup.cables.sort(SORT_BY_INDEX);
        }

        if (branchState && (branchState.A.length > 0 || branchState.B.length > 0)) {
            sheathGroup.cableBranches[cable.originalIndex] = branchState;
        }
    };

    allSubGroups.forEach((group, groupIdx) => {
        const remainingCables = group.cables
            .filter(cable => !consumedStandardIndexes.has(cable.originalIndex))
            .sort(SORT_BY_INDEX);

        if (remainingCables.length === 0) {
            return;
        }

        if (remainingCables.length === 1) {
            const cable = remainingCables[0];
            if (cable.guaina || forcedSheathId) {
                appendToSheathGroup(cable, null, forcedSheathId);
            } else {
                renderItems.push({
                    type: 'single',
                    sortIndex: cable.originalIndex,
                    cable,
                    key: `single-${cable.originalIndex}`
                });
            }
            consumedStandardIndexes.add(cable.originalIndex);
            return;
        }

        const sheathAnchors = remainingCables.filter(cable => cable.guaina);
        const nestedSheathDescriptor = buildSheathBranches(
            remainingCables,
            settings,
            sheathAnchors.length > 0 ? sheathAnchors : remainingCables
        );

        if (nestedSheathDescriptor && (nestedSheathDescriptor.anchorCable?.guaina || forcedSheathId)) {
            appendToSheathGroup(nestedSheathDescriptor.anchorCable, nestedSheathDescriptor.allBranches, forcedSheathId);
            remainingCables.forEach(cable => consumedStandardIndexes.add(cable.originalIndex));
            return;
        }

        const branchedDescriptor = getBranchedGroupDescriptor(
            remainingCables,
            settings,
            sheathAnchors.length > 0 ? sheathAnchors : remainingCables
        );

        if (branchedDescriptor && (branchedDescriptor.anchorCable?.guaina || forcedSheathId)) {
            appendToSheathGroup(branchedDescriptor.anchorCable, branchedDescriptor.allBranches, forcedSheathId);
            remainingCables.forEach(cable => consumedStandardIndexes.add(cable.originalIndex));
            return;
        }

        renderItems.push({
            type: 'multiple',
            sortIndex: Math.min(...remainingCables.map(cable => cable.originalIndex)),
            group: {
                ...group,
                cables: remainingCables
            },
            groupIdx,
            key: `multi-${Math.min(...remainingCables.map(cable => cable.originalIndex))}`
        });
        remainingCables.forEach(cable => consumedStandardIndexes.add(cable.originalIndex));
    });

    multipolarCables.forEach((cable) => {
        renderItems.push({
            type: 'multipolar',
            sortIndex: cable.originalIndex,
            cable,
            poleBranches: multipolarBranchMap.get(cable.originalIndex) || {},
            key: `multipolar-${cable.originalIndex}`
        });
    });

    sheathGroups.forEach(({ sheathId, cables: groupedCables, cableBranches }) => {
        renderItems.push({
            type: 'sheath',
            sortIndex: Math.min(...groupedCables.map(cable => cable.originalIndex)),
            sheathId,
            cables: groupedCables,
            cableBranches,
            key: `sheath-${sheathId}-${groupedCables.map(cable => cable.originalIndex).join('-')}`
        });
    });

    renderItems.sort((a, b) => a.sortIndex - b.sortIndex);

    return (
        <div className="space-y-4">
            {renderItems.map((item) => {
                if (item.type === 'multipolar') {
                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-cable-index={item.cable.originalIndex}>
                            <MultipolarCableVisualization
                                cable={item.cable}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                                poleBranches={item.poleBranches}
                            />
                        </div>
                    );
                }

                if (item.type === 'multiple') {
                    const cablesWithSheath = item.group.cables.filter(c => c.guaina);
                    const sheathId = cablesWithSheath.length > 0 ? cablesWithSheath[0].guaina : null;
                    const sheath = sheathId ? availableSheaths.find(s => Number(s.id) === Number(sheathId)) : null;

                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-cable-indices={item.group.cables.map(c => c.originalIndex).join(',')}>
                            <MultipleCableVisualization
                                cables={item.group.cables}
                                siglatura={`${siglatura} (${t('schematic.group')} ${item.groupIdx + 1})`}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                                sheath={sheath}
                                availableSheaths={availableSheaths}
                            />
                        </div>
                    );
                }

                if (item.type === 'sheath') {
                    const sheath = availableSheaths.find(s => s.id === parseInt(item.sheathId, 10));
                    const sheathLabel = sheath ? (sheath.fullLabel || sheath.label) : `${t('schematic.sheath')} ${item.sheathId}`;

                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-sheath-id={item.sheathId}>
                            <SheathCableVisualization
                                cables={item.cables}
                                sheathLabel={sheathLabel}
                                sheathType={sheath?.type}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                                cableBranches={item.cableBranches}
                            />
                        </div>
                    );
                }

                if (item.type === 'single') {
                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-cable-index={item.cable.originalIndex}>
                            <SingleCableVisualization
                                cable={item.cable}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                            />
                        </div>
                    );
                }

                return null;
            })}
        </div>
    );
};

export default HybridCableVisualization;
